'use client';

import { useCallback, useState, useMemo, useRef, useEffect, useLayoutEffect } from 'react';

//* HOC's
import { withUIContext, withFavoriteContext } from '@/context';

//* Components
import Icon from '@/components/common/Icon';
import CustomImage from '@/components/common/Image';
import ProductBadges from '@/components/common/ProductBadges';

//* Style
import ProductImageGalleryStyle from './style';

const ProductImageGallery = ({ image, gallery = [], productId, badges = [], winWidth, toggleFavorite, checkActiveFavorite }) => {
	//! Combine featured image with gallery images, deduplicating by src
	const allImages = useMemo(() => {
		// Helper function to normalize image src for comparison
		const normalizeSrc = (img) => {
			if (!img) return null;
			return typeof img === 'string' ? img : img.src || img.url || null;
		};

		const result = [];
		const seenSrcs = new Set();

		// Add featured image first if it exists
		if (image) {
			const featuredSrc = normalizeSrc(image);
			if (featuredSrc) {
				result.push(image);
				seenSrcs.add(featuredSrc);
			}
		}

		// Add gallery images, skipping duplicates
		if (gallery && Array.isArray(gallery) && gallery.length > 0) {
			gallery.forEach((galleryImage) => {
				const gallerySrc = normalizeSrc(galleryImage);
				if (gallerySrc && !seenSrcs.has(gallerySrc)) {
					seenSrcs.add(gallerySrc);
					result.push(galleryImage);
				}
			});
		}

		return result;
	}, [image, gallery]);

	//! Refs
	const mainImageWrapperRef = useRef(null);
	const mainImageRef = useRef(null);
	const thumbnailGalleryRef = useRef(null);
	const lightboxOverlayRef = useRef(null);
	const [thumbnailHeight, setThumbnailHeight] = useState(null);

	//! State
	const [selectedIndex, setSelectedIndex] = useState(0);
	const [backgroundPosition, setBackgroundPosition] = useState({ backgroundPosition: '0% 0%' });
	const [isLightboxOpen, setIsLightboxOpen] = useState(false);
	const [isHovering, setIsHovering] = useState(false);

	//! Checkers
	const checkers = useMemo(() => {
		return (winWidth >= 1024 && winWidth < 1280) || winWidth < 768;
	}, [winWidth]);

	//! Lock body scroll when lightbox is open and handle keyboard navigation
	useEffect(() => {
		if (isLightboxOpen) {
			// Save current scroll position
			const scrollY = window.scrollY;
			document.body.style.position = 'fixed';
			document.body.style.top = `-${scrollY}px`;
			document.body.style.width = '100%';
			document.body.style.overflow = 'hidden';

			// Keyboard navigation
			const handleKeyDown = (e) => {
				// Close on Escape
				if (e.key === 'Escape') {
					setIsLightboxOpen(false);
					return;
				}

				// Navigate images with arrow keys
				if (e.key === 'ArrowLeft') {
					setSelectedIndex((prev) => (prev - 1 + allImages.length) % allImages.length);
					setBackgroundPosition({ backgroundPosition: '0% 0%' });
				} else if (e.key === 'ArrowRight') {
					setSelectedIndex((prev) => (prev + 1) % allImages.length);
					setBackgroundPosition({ backgroundPosition: '0% 0%' });
				}

				// Prevent keyboard scrolling
				if (['ArrowUp', 'ArrowDown', 'PageUp', 'PageDown', 'Home', 'End', ' '].includes(e.key)) {
					e.preventDefault();
				}
			};

			window.addEventListener('keydown', handleKeyDown);

			return () => {
				// Restore scroll position
				const bodyTop = document.body.style.top;
				document.body.style.position = '';
				document.body.style.top = '';
				document.body.style.width = '';
				document.body.style.overflow = '';
				window.scrollTo(0, parseInt(bodyTop || '0') * -1);

				window.removeEventListener('keydown', handleKeyDown);
			};
		}
	}, [isLightboxOpen, allImages.length]);

	//! Prevent touch scroll on lightbox overlay; only touchmove so taps still fire click and close modal
	useEffect(() => {
		if (!isLightboxOpen) return;
		const el = lightboxOverlayRef.current;
		if (!el) return;
		const preventTouchMove = (e) => e.preventDefault();
		el.addEventListener('touchmove', preventTouchMove, { passive: false });
		return () => {
			el.removeEventListener('touchmove', preventTouchMove);
		};
	}, [isLightboxOpen]);

	//! Calculate thumbnail height based on main image height
	useEffect(() => {
		const updateThumbnailHeight = () => {
			// Use the image element itself, not the wrapper, to avoid layout loops
			const imageElement = mainImageRef.current?.parentElement?.querySelector('.product-image');

			if (imageElement && thumbnailGalleryRef.current) {
				const imageHeight = imageElement.offsetHeight;
				if (imageHeight > 0) {
					// Get the actual gap value from computed styles
					const computedStyle = window.getComputedStyle(thumbnailGalleryRef.current);
					const gapValue = parseFloat(computedStyle.gap) || 8; // Default to 8px if gap not found

					// Calculate: (main image height - 3 gaps) / 4
					// 4 thumbnails = 3 gaps between them
					const numberOfGaps = 3;
					const calculatedHeight = (imageHeight - numberOfGaps * gapValue) / 4;

					setThumbnailHeight((prevHeight) => {
						// Only update if change is significant (more than 3px) to prevent loops
						if (prevHeight === null || Math.abs(prevHeight - calculatedHeight) > 3) {
							return calculatedHeight;
						}
						return prevHeight;
					});
				}
			} else if (mainImageWrapperRef.current && thumbnailGalleryRef.current) {
				// Fallback to wrapper if image element not found
				const wrapperHeight = mainImageWrapperRef.current.offsetHeight;
				if (wrapperHeight > 0) {
					const computedStyle = window.getComputedStyle(thumbnailGalleryRef.current);
					const gapValue = parseFloat(computedStyle.gap) || 8;
					const numberOfGaps = 3;
					const calculatedHeight = (wrapperHeight - numberOfGaps * gapValue) / 4;

					setThumbnailHeight((prevHeight) => {
						if (prevHeight === null || Math.abs(prevHeight - calculatedHeight) > 3) {
							return calculatedHeight;
						}
						return prevHeight;
					});
				}
			}
		};

		// Wait for image to load
		const timeoutId = setTimeout(() => {
			updateThumbnailHeight();
		}, 300);

		// Only listen to window resize, not ResizeObserver to avoid loops
		let resizeTimeout;
		const debouncedResize = () => {
			clearTimeout(resizeTimeout);
			resizeTimeout = setTimeout(updateThumbnailHeight, 200);
		};

		window.addEventListener('resize', debouncedResize);

		return () => {
			clearTimeout(timeoutId);
			clearTimeout(resizeTimeout);
			window.removeEventListener('resize', debouncedResize);
		};
	}, [allImages.length]);

	//! Current image
	const currentImage = allImages[selectedIndex] || allImages[0];

	//! Helper function to get image src safely
	const getImageSrc = useCallback((img) => {
		if (!img) return null;
		return typeof img === 'string' ? img : img.src || img.url || null;
	}, []);

	//! If pointer was already over the figure (no mouseenter), keep isHovering in sync with CSS :hover
	const syncHoverIfPointerAlreadyOverFigure = useCallback(() => {
		if (winWidth < 1024) return;
		const el = mainImageRef.current;
		if (el?.matches?.(':hover')) {
			setIsHovering(true);
		}
	}, [winWidth]);

	useLayoutEffect(() => {
		syncHoverIfPointerAlreadyOverFigure();
	}, [syncHoverIfPointerAlreadyOverFigure, selectedIndex, currentImage]);

	//! Handle mouse move for zoom effect
	const handleMouseMove = useCallback(
		(e) => {
			if (winWidth < 1024 || !currentImage) return;

			const target = e.currentTarget || e.target;
			const { left, top, width, height } = target.getBoundingClientRect();
			const x = ((e.pageX - left) / width) * 100;
			const y = ((e.pageY - top) / height) * 50;

			setBackgroundPosition({ backgroundPosition: `${x}% ${y}%` });
		},
		[winWidth, currentImage]
	);

	//! Handlers
	const handleFavoriteClick = useCallback(() => {
		toggleFavorite({ productId });
	}, [toggleFavorite, productId]);

	const handleThumbnailClick = useCallback((index) => {
		setSelectedIndex(index);
		setBackgroundPosition({ backgroundPosition: '0% 0%' });
	}, []);

	const handleMainImageClick = useCallback(() => {
		if (allImages.length > 0) {
			setIsLightboxOpen(true);
		}
	}, [allImages.length]);

	const handleNextImage = useCallback(() => {
		setSelectedIndex((prev) => (prev + 1) % allImages.length);
		setBackgroundPosition({ backgroundPosition: '0% 0%' });
	}, [allImages.length]);

	const handlePrevImage = useCallback(() => {
		setSelectedIndex((prev) => (prev - 1 + allImages.length) % allImages.length);
		setBackgroundPosition({ backgroundPosition: '0% 0%' });
	}, [allImages.length]);

	const handleCloseLightbox = useCallback(() => {
		setIsLightboxOpen(false);
	}, []);

	const isFavorite = checkActiveFavorite({ productId });

	if (!currentImage) {
		return null;
	}

	return (
		<ProductImageGalleryStyle>
			<div className='gallery-container'>
				{/* Thumbnail Gallery - Left Side */}
				{allImages.length > 1 && (
					<div
						ref={thumbnailGalleryRef}
						className={`thumbnail-gallery ${allImages.length > 4 ? 'scrollable' : ''}`}
						style={!checkers ? (thumbnailHeight ? { '--thumbnail-height': `${thumbnailHeight}px` } : {}) : {}}
					>
						{allImages.map((img, index) => {
							const imgSrc = getImageSrc(img);
							if (!imgSrc) return null;
							return (
								<button
									key={`thumbnail-${imgSrc}-${index}`}
									type='button'
									className={`thumbnail-item ${selectedIndex === index ? 'active' : ''}`}
									onClick={() => handleThumbnailClick(index)}
									aria-label={`View image ${index + 1}`}
									style={!checkers ? (thumbnailHeight ? { height: `${thumbnailHeight}px` } : {}) : thumbnailHeight ? { width: `${thumbnailHeight}px` } : {}}
								>
									<CustomImage
										src={imgSrc}
										alt={img?.alt || `Product image ${index + 1}`}
										className={'thumbnail-image'}
									/>
								</button>
							);
						})}
					</div>
				)}

				{/* Main Image - Right Side */}
				<div
					className='main-image-wrapper'
					ref={mainImageWrapperRef}
				>
					{/* Product Badges - Top Left */}
					<ProductBadges
						badges={badges}
						className='product-badges'
					/>

					<figure
						ref={mainImageRef}
						onMouseMove={handleMouseMove}
						onMouseEnter={() => {
							if (winWidth >= 1024) {
								setIsHovering(true);
							}
						}}
						onMouseLeave={() => {
							setIsHovering(false);
							setBackgroundPosition({ backgroundPosition: '0% 0%' });
						}}
						onClick={handleMainImageClick}
						className={`product-image-figure ${winWidth >= 1024 ? 'desktop-version' : ''} ${allImages.length > 1 ? 'clickable' : ''}`}
						style={winWidth >= 1024 && currentImage && isHovering ? { ...backgroundPosition, backgroundImage: `url(${getImageSrc(currentImage)})` } : null}
					>
						<CustomImage
							src={getImageSrc(currentImage)}
							alt={currentImage?.alt || 'Product image'}
							className={'product-image'}
							priority={true}
							onLoad={() => {
								// Recalculate when image loads
								setTimeout(() => {
									syncHoverIfPointerAlreadyOverFigure();
									if (mainImageRef.current && thumbnailGalleryRef.current) {
										const imageElement = mainImageRef.current.querySelector('.product-image');
										if (imageElement) {
											const imageHeight = imageElement.offsetHeight;
											if (imageHeight > 0) {
												const computedStyle = window.getComputedStyle(thumbnailGalleryRef.current);
												const gapValue = parseFloat(computedStyle.gap) || 8;
												const numberOfGaps = 3;
												const calculatedHeight = (imageHeight - numberOfGaps * gapValue) / 4;
												setThumbnailHeight(calculatedHeight);
											}
										}
									}
								}, 100);
							}}
						/>
					</figure>

					{/* Favorite Icon */}
					<Icon
						className={'favorite'}
						onClick={handleFavoriteClick}
						name={isFavorite ? 'heart-fill' : 'heart'}
					>
						<span className='path1'></span>
						<span className='path2'></span>
					</Icon>
				</div>
			</div>

			{/* Lightbox Modal */}
			{isLightboxOpen && (
				<div
					ref={lightboxOverlayRef}
					className='lightbox-overlay'
					onClick={handleCloseLightbox}
					onMouseDown={(e) => e.preventDefault()}
				>
					<button
						type='button'
						className='lightbox-close'
						onClick={handleCloseLightbox}
						aria-label='Close lightbox'
					>
						<svg
							width='20'
							height='20'
							viewBox='0 0 24 24'
							fill='none'
							xmlns='http://www.w3.org/2000/svg'
						>
							<path
								d='M18 6L6 18M6 6L18 18'
								stroke='currentColor'
								strokeWidth='2'
								strokeLinecap='round'
								strokeLinejoin='round'
							/>
						</svg>
					</button>

					<div
						className='lightbox-content'
						onClick={(e) => e.stopPropagation()}
					>
						{/* Lightbox Thumbnails */}
						{allImages.length > 1 && (
							<div className='lightbox-thumbnails'>
								{allImages.map((img, index) => {
									const imgSrc = getImageSrc(img);
									if (!imgSrc) return null;
									return (
										<button
											key={`lightbox-thumbnail-${imgSrc}-${index}`}
											type='button'
											className={`lightbox-thumbnail ${selectedIndex === index ? 'active' : ''}`}
											onClick={() => handleThumbnailClick(index)}
											aria-label={`View image ${index + 1}`}
										>
											<CustomImage
												src={imgSrc}
												alt={img?.alt || `Product image ${index + 1}`}
												className={'thumbnail-image'}
											/>
										</button>
									);
								})}
							</div>
						)}

						{/* Lightbox Main Image */}
						<div className='lightbox-main-image'>
							<CustomImage
								src={getImageSrc(currentImage)}
								alt={currentImage?.alt || 'Product image'}
								className={'lightbox-image'}
							/>

							{/* Lightbox Navigation */}
							{allImages.length > 1 && (
								<>
									<button
										type='button'
										className='lightbox-nav lightbox-nav-prev'
										onClick={handlePrevImage}
										aria-label='Previous image'
									>
										<svg
											width='24'
											height='24'
											viewBox='0 0 24 24'
											fill='none'
											xmlns='http://www.w3.org/2000/svg'
										>
											<path
												d='M15 18L9 12L15 6'
												stroke='currentColor'
												strokeWidth='2'
												strokeLinecap='round'
												strokeLinejoin='round'
											/>
										</svg>
									</button>
									<button
										type='button'
										className='lightbox-nav lightbox-nav-next'
										onClick={handleNextImage}
										aria-label='Next image'
									>
										<svg
											width='24'
											height='24'
											viewBox='0 0 24 24'
											fill='none'
											xmlns='http://www.w3.org/2000/svg'
										>
											<path
												d='M9 6L15 12L9 18'
												stroke='currentColor'
												strokeWidth='2'
												strokeLinecap='round'
												strokeLinejoin='round'
											/>
										</svg>
									</button>
								</>
							)}
						</div>
					</div>
				</div>
			)}
		</ProductImageGalleryStyle>
	);
};

export default withUIContext(withFavoriteContext(ProductImageGallery, ['toggleFavorite', 'checkActiveFavorite']), ['winWidth']);
